import type { AxiosError, AxiosInstance } from 'axios'
import type { APIError } from '../types/api'

declare module 'axios' {
  // Параметр типа повторяет объявление в axios: слияние интерфейсов требует тот же набор
  interface AxiosRequestConfig<D = any> {
    /**
     * Не уводить на /login, если на 401 токен не удалось обновить: ошибка уходит
     * вызывающему как APIError со status 401, и экран сам говорит «сессия истекла».
     * Нужно там, где редирект посреди работы теряет то, что открыто на экране
     * (голосовая перекличка). Учитывается интерцептором web-app.
     */
    skipAuthRedirect?: boolean
  }
}

let _apiClient: AxiosInstance | null = null

export function setApiClient(client: AxiosInstance): void {
  _apiClient = client
}

export function getApiClient(): AxiosInstance {
  if (!_apiClient) {
    throw new Error(
      '@procoach/shared: API client not initialized. Call setApiClient() in your app entry point.'
    )
  }
  return _apiClient
}

/**
 * Приводит ошибку axios к APIError - единственной форме, которую интерцепторы
 * приложений отдают наружу. Поля терять нельзя: code и context нужны ошибкам
 * лимитов и soft lock, user_message и ai_usage - AI-ручкам (чат, голос).
 */
export function toAPIError(error: AxiosError): APIError {
  if (error.response) {
    const data = error.response.data as any
    return {
      error: data?.error || data?.message || 'Unknown error',
      details: data?.details,
      code: data?.code,
      message: data?.message,
      context: data?.context,
      user_message: data?.user_message,
      ai_usage: data?.ai_usage,
      status: error.response.status,
    }
  }
  if (error.request) {
    return { error: 'Network error', details: 'Could not connect to server' }
  }
  return { error: 'Request error', details: error.message }
}

// Default export via Proxy — backward compatible with all API modules
// that do `import apiClient from './client'`
const apiClient = new Proxy({} as AxiosInstance, {
  get(_target, prop: string | symbol) {
    return (getApiClient() as any)[prop]
  },
})

export default apiClient
